import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { selectApiVersion } from "shared/store/configSlice";
import { useHandbookSearchPagination } from "features/handbook/hooks/useHandbookSearchPagination";
import { useNavTitle } from "features/handbookList/hooks/useNavTitle";
import {
  getHandbookFieldFlags,
  getRequiredFields,
} from "features/handbook/lib/handbookFieldFlags";
import { HandbookEditModal } from "./HandbookEditModal";
import { HandbookPageHeader } from "./HandbookPageHeader";
import {
  HandbookBindingView,
  HandbookListView,
  pickListFlags,
  pickRefFields,
  pickSharedFlags,
} from "./HandbookViews";
import { ServiceCategorySection } from "./ServiceCategorySection";
import { useHandbookCrud } from "./useHandbookCrud";
import { useHandbookLists } from "./useHandbookLists";
import { useHandbookRoute } from "./useHandbookRoute";
import { useServiceBinding } from "./useServiceBinding";
import { useServiceCategorySection } from "./useServiceCategorySection";
import { useServiceTitle } from "./useServiceTitle";
import styles from "./HandbookPage.module.css";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_SIZE_ALL = 10;

export default function HandbookPage() {
  const { t } = useTranslation();
  const apiVersion = useSelector(selectApiVersion);

  const route = useHandbookRoute();
  const { dictCode, serviceCode, isServiceCategory, isAllServices } = route;

  const { title: navTitle } = useNavTitle({
    code: dictCode,
    path: route.pathname,
  });

  const {
    pagination,
    allDictPagination,
    search,
    debouncedSearch,
    handleChangeSearch,
    handlePageChange,
    handleAllDictPageChange,
  } = useHandbookSearchPagination({
    defaultPageSize: DEFAULT_PAGE_SIZE,
    defaultAllPageSize: DEFAULT_PAGE_SIZE_ALL,
    resetKey: dictCode,
  });

  const serviceTitle = useServiceTitle(serviceCode);

  const lists = useHandbookLists({
    dictCode,
    serviceCode,
    pagination,
    allDictPagination,
    debouncedSearch,
    apiVersion,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    defaultAllPageSize: DEFAULT_PAGE_SIZE_ALL,
  });

  const flags = getHandbookFieldFlags(lists.rows, dictCode);

  const crud = useHandbookCrud({
    dictCode,
    reloadCurrent: lists.reloadCurrent,
  });

  const binding = useServiceBinding({
    dictCode,
    serviceCode,
    serviceTitle,
    reloadBoth: lists.reloadBoth,
  });

  const serviceCategory = useServiceCategorySection({
    dictCode,
    serviceCode,
    page: lists.allDictPage,
    pageSize: lists.allDictPageSize,
    debouncedSearch,
    level: route.level,
    ancestorId: route.ancestorId,
    section: route.categorySection,
  });

  /** Режим привязки: сервис выбран, значит показываем две таблицы. */
  const isBindingMode = !isAllServices && !isServiceCategory;

  const commonListProps = {
    onEdit: crud.openEdit,
    onDelete: crud.handleDelete,
    isAgroCredit: route.isAgroCredit,
  };

  const allDictList = {
    ...commonListProps,
    rows: lists.allDictRows,
    isFetching: lists.isFetchingAll,
    pagination: {
      current: lists.allDictPage,
      pageSize: lists.allDictPageSize,
      total: lists.allDictTotal,
    },
    onPageChange: handleAllDictPageChange,
  };

  return (
    <div className={styles.page}>
      <HandbookPageHeader
        title={navTitle || t("handbook.title", "Справочник")}
        dictCode={dictCode}
        search={search}
        onSearchChange={handleChangeSearch}
        onCreate={
          isBindingMode
            ? undefined
            : isServiceCategory
              ? serviceCategory.openCreate
              : crud.openCreate
        }
      />

      {isBindingMode ? (
        <HandbookBindingView
          serviceTitle={serviceTitle}
          serviceCode={serviceCode}
          attached={{
            ...commonListProps,
            rows: lists.rows,
            isFetching: lists.isFetching,
            pagination: {
              current: lists.page,
              pageSize: lists.pageSize,
              total: lists.total,
            },
            onPageChange: handlePageChange,
          }}
          available={allDictList}
          flags={pickSharedFlags(flags)}
          refFields={pickRefFields(flags)}
          onAttachToService={binding.attach}
          onDetachFromService={binding.detach}
          onAttachAll={binding.attachAll}
          onDetachAll={binding.detachAll}
          isAddingAll={binding.isAddingAll}
          isRemovingAll={binding.isRemovingAll}
        />
      ) : (
        !isServiceCategory && (
          <HandbookListView
            list={allDictList}
            flags={pickListFlags(flags)}
            refFields={pickRefFields(flags)}
          />
        )
      )}

      {!isServiceCategory && (
        <HandbookEditModal
          open={crud.modalOpen}
          loading={crud.isSaving}
          initial={crud.editingRow}
          onCancel={crud.closeModal}
          onSubmit={crud.handleSubmit}
          flags={flags}
          requiredFields={getRequiredFields(dictCode)}
          isTerritorialUnit={route.isTerritorialUnit}
          isAgroCredit={route.isAgroCredit}
        />
      )}

      {isServiceCategory && (
        <ServiceCategorySection
          section={serviceCategory}
          pagination={{
            current: lists.allDictPage,
            pageSize: lists.allDictPageSize,
            total: serviceCategory.cardsTotal,
          }}
          onPageChange={handleAllDictPageChange}
          level={route.level}
          ancestorId={route.ancestorId}
          canGoUp={!(route.pathIds.length === 0 && route.level === 0)}
          onOpenCategory={route.openCategory}
          onGoToParent={route.goToParentCategory}
          onGoToRoot={route.goToRootCategory}
          onSectionChange={(next) => {
            route.setCategorySection(next);
            handleAllDictPageChange(1, lists.allDictPageSize);
          }}
        />
      )}
    </div>
  );
}
